"use client";

import {
  type DependencyList,
  type EffectCallback,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { COOKIE_CONSENT_COOKIE, isCookieAllowed, parseCookieConsent } from "shared/CookieConsent";
import { FIRST_PARTY_COOKIES } from "shared/SharedConstants";
import { setQueryParam } from "src/lib/MiscFrontendUtils";
import { useSearchParams } from "src/lib/Navigation";
import { getRequestCookies, getRequestUrl } from "src/lib/RequestState";
import {
  getCookie,
  getServerViewportWidth,
  getSiteCookieAttributes,
  serializeCookie,
  setUnconsentedCookie,
} from "../AppSharedUtils";

// Global cookie change listeners system
const COOKIE_CHANGE_LISTENERS = new Map<string, Set<() => void>>();

// Global query params change listeners system
const QUERY_PARAMS_CHANGE_LISTENERS = new Set<() => void>();
let queryParamsInterval: NodeJS.Timeout | undefined;
let lastSearchParams: string | undefined;

const startQueryParamsPolling = () => {
  if (queryParamsInterval || typeof window === "undefined") {
    return;
  }

  lastSearchParams = window.location.search;
  queryParamsInterval = setInterval(() => {
    if (window.location.search !== lastSearchParams && queryParamUpdatesEnabled) {
      lastSearchParams = window.location.search;
      // Trigger all query params listeners
      for (const listener of QUERY_PARAMS_CHANGE_LISTENERS) {
        listener();
      }
    }
  }, 100);
};

const stopQueryParamsPolling = () => {
  if (queryParamsInterval) {
    clearInterval(queryParamsInterval);
    queryParamsInterval = undefined;
  }
};

const subscribeToQueryParamsChanges = (listener: () => void) => {
  QUERY_PARAMS_CHANGE_LISTENERS.add(listener);
  startQueryParamsPolling();

  return () => {
    QUERY_PARAMS_CHANGE_LISTENERS.delete(listener);
    // Stop polling if no more listeners
    if (QUERY_PARAMS_CHANGE_LISTENERS.size === 0) {
      stopQueryParamsPolling();
    }
  };
};

export const triggerCookieChange = (cookieName: string) => {
  for (const listener of COOKIE_CHANGE_LISTENERS.get(cookieName) ?? []) {
    listener();
  }
};

let queryParamUpdatesEnabled = true;

export const disableQueryParamUpdates = () => {
  queryParamUpdatesEnabled = false;
};

export function useQueryParams() {
  const oldSearch = typeof window === "undefined" ? undefined : window.location.search;
  const searchParams =
    typeof window === "undefined"
      ? useSearchParams()
      : useMemo(
          () => new URLSearchParams(queryParamUpdatesEnabled ? window.location.search : oldSearch),
          [window.location.search],
        );
  const [forceUpdate, key] = useForceUpdate();

  useEffect(() => {
    // Subscribe to global query params changes
    return subscribeToQueryParamsChanges(() => {
      forceUpdate();
    });
  }, [forceUpdate]);

  const createQueryString = useCallback(
    (name: string, value: string | null | undefined) => {
      const params = new URLSearchParams(searchParams);

      if (value === null || value === undefined) {
        params.delete(name);
      } else {
        params.set(name, value);
      }

      return params.toString();
    },
    [searchParams],
  );

  return { queryParams: searchParams, createQueryString, setQueryParam };
}

// Callbacks waiting for the initial render; emptied when it is done.
const INITIAL_RENDER_CALLBACKS: Array<() => void> = [];

let INITIAL_RENDER_DONE = false;

/**
 * Runs `callback` once the initial render is done, or right away if it already is. Returns a
 * function that withdraws a callback still waiting (for an effect's cleanup).
 */
const afterInitialRender = (callback: () => void) => {
  if (INITIAL_RENDER_DONE) {
    callback();
    return () => {};
  }
  INITIAL_RENDER_CALLBACKS.push(callback);
  return () => {
    const index = INITIAL_RENDER_CALLBACKS.indexOf(callback);
    if (index >= 0) INITIAL_RENDER_CALLBACKS.splice(index, 1);
  };
};

/** How many callbacks wait for the initial render; for tests. */
export const getInitialRenderCallbackCount = () => INITIAL_RENDER_CALLBACKS.length;

export const setInitialRenderDone = () => {
  if (!INITIAL_RENDER_DONE) {
    INITIAL_RENDER_DONE = true;
    for (const cb of INITIAL_RENDER_CALLBACKS.splice(0)) {
      cb();
    }
  }
};

const getScreenWidth = (): number | undefined => {
  if (typeof window === "undefined") {
    return getServerViewportWidth();
  }

  // go with what the server rendered as the width to start with if we're on the client
  if (!INITIAL_RENDER_DONE) {
    const cookie = getCookie(FIRST_PARTY_COOKIES.PRE_RENDERED_WIDTH.name);
    return cookie ? Number.parseInt(cookie, 10) : undefined;
  }

  return window.innerWidth;
};

export const useBreakpoint = (breakpoint: { min?: number; max?: number }): boolean | undefined => {
  "use no memo";
  const checkBreakpoint = useCallback(() => {
    const width = getScreenWidth();
    if (width === undefined) {
      return undefined;
    }
    return width >= (breakpoint.min ?? 0) && width <= (breakpoint.max ?? Number.MAX_SAFE_INTEGER);
  }, [breakpoint]);

  const [isBreakpoint, setIsBreakpoint] = useState<boolean | undefined>(() => checkBreakpoint());

  const handleResize = useCallback(() => {
    const newIsBreakpoint = checkBreakpoint() ?? false;
    // React skips the re-render when the value hasn't changed.
    setIsBreakpoint(newIsBreakpoint);
    return newIsBreakpoint;
  }, [checkBreakpoint]);

  // The server rendered with the cookie's width; measure the real one once the page has rendered.
  // Registered once per mount (not per render), calling whatever `handleResize` is current by then.
  const latestHandleResize = useRef(handleResize);
  useEffect(() => {
    latestHandleResize.current = handleResize;
  }, [handleResize]);
  useEffect(
    () =>
      afterInitialRender(() => {
        latestHandleResize.current();
      }),
    [],
  );

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  return isBreakpoint;
};

export { useDebouncedCallback } from "shared-web/lib/useDebouncedCallback";

export const useForceUpdate = (): [() => void, number] => {
  const [state, setState] = useState(0);
  return [() => setState((s) => s + 1), state];
};

export function useUpdateEffect(effect: EffectCallback, deps: DependencyList = []): void {
  const isMounted = useRef(false);

  // for react-refresh
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      return effect();
    }
  }, deps);
}

/**
 * Writes (or with `null`, deletes) a cookie. Cookies the inventory marks as non-essential are only
 * written when the user has allowed their category; otherwise the value is kept in memory for
 * this page session so the feature still works. Deleting is always allowed.
 */
export const setCookie = (name: string, value: string | null, options?: { expiresMs?: number }) => {
  const existingCookie = getCookie(name);
  if (value === null) {
    setUnconsentedCookie(name, null);
  } else if (!isCookieAllowed(name, parseCookieConsent(getCookie(COOKIE_CONSENT_COOKIE.name)))) {
    setUnconsentedCookie(name, value, options?.expiresMs);
    if (existingCookie !== value) {
      triggerCookieChange(name);
    }
    return;
  }

  const expires = options?.expiresMs ? new Date(Date.now() + options.expiresMs) : undefined;
  if (typeof document === "undefined") {
    const cookies = getRequestCookies();
    const requestUrl = getRequestUrl();
    if (cookies) {
      if (!value) {
        cookies.delete(name);
      } else {
        cookies.set(name, value, {
          path: "/",
          expires: expires,
          ...(requestUrl ? getSiteCookieAttributes(requestUrl) : {}),
        });
      }
    }
  } else {
    // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is not universally available in supported browsers.
    document.cookie = serializeCookie(name, value || "", {
      expires: value ? expires : new Date(0),
      ...getSiteCookieAttributes(window.location),
    });
  }

  if (existingCookie !== value) {
    triggerCookieChange(name);
  }
};

export const useCookie = (cookieName: string) => {
  "use no memo";

  const [forceUpdate, key] = useForceUpdate();

  useEffect(() => {
    const listener = () => forceUpdate();

    // Get or create the Set of listeners for this cookie
    let listeners = COOKIE_CHANGE_LISTENERS.get(cookieName);
    if (!listeners) {
      listeners = new Set();
      COOKIE_CHANGE_LISTENERS.set(cookieName, listeners);
    }

    listeners.add(listener);
    return () => {
      listeners?.delete(listener);
      // Clean up empty Sets to prevent memory leaks
      if (listeners?.size === 0) {
        COOKIE_CHANGE_LISTENERS.delete(cookieName);
      }
    };
  }, [cookieName, forceUpdate]);

  const _setCookie = useCallback(
    (value: string | null, options?: { expiresMs?: number }) => {
      setCookie(cookieName, value, options);
    },
    [cookieName],
  );

  const _getCookie = useCallback((): string | undefined => {
    return getCookie(cookieName);
  }, [cookieName, key]);

  return [_getCookie, _setCookie] as const;
};
